// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsEnvelopes from '../../../api/actionsEnvelopes';
import useEnvelopesStore from '@/app/store/modules/envelopes';
// Components
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import EnvelopesTable from '../../tables/EnvelopesTable';
import FooterClose from '@/common-utils/components/renders/modal/components/footers/FooterClose/FooterClose';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsEnvelope } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalDeprogramEnvelopes
 */
function ModalDeprogramEnvelopes({ isOpen, setIsOpen }) {

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.deprogram.steps';

    // Store
    const storeRefetchEnvelopes = useEnvelopesStore(s => s.storeRefetchEnvelopes);

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);
    const [envelopesSelected, setEnvelopesSelected] = useState(getInitialValueForRowSelected());
    const [errorMessage, setErrorMessage] = useState();
    const [notDeleted, setNotDeleted] = useState([]);

    // Api calls
    const onSuccess = (response) => {
        const { notDeleted: notDeletedEnvelopes } = response.data;

        if (notDeletedEnvelopes.length) {
            setNotDeleted(notDeletedEnvelopes);
            setStep(step + 1);
        }
        else {
            setIsOpen(false);
            toast({
                description: <Entity namespace={nsEnvelope} entity='modal.deprogram.success' />,
                variant: 'success'
            });
        }
        storeRefetchEnvelopes(true);
    };
    const onError = (error) => {
        setErrorMessage(error.response?.data?.errorCode ?? error.code ?? 'UNKNOWN_ERROR');
        setStep(step + 1);
    };
    const deprogramEnvelopes = actionsEnvelopes.deprogramEnvelopes(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext, modalSize } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.envelopes_selection`,
                disabledNext: !Object.keys(envelopesSelected.rowIds).length,
                modalSize: 'max-w-4xl'
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirm`,
                disabledNext: false,
                modalSize: 'max-w-2xl'
            }
        }),
        [envelopesSelected]
    );

    // Render
    return <Modal open={isOpen} onOpenChange={setIsOpen}>
        <ModalContent className={modalSize}>
            <HeaderTitle
                title={'modal.deprogram.title'}
                namespace={nsEnvelope}
            />
            <ModalBody className='modal-max-height overflow-auto'>
                {step < 2 &&
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsEnvelope}
                        className={'mb-2 flex'}
                    />
                }
                {step === 0 &&
                    <EnvelopesTable
                        filtersEventName={setFiltersEventName('envelopes', true)}
                        rowsSelected={envelopesSelected}
                        setRowsSelected={setEnvelopesSelected}
                    />
                }
                {step === 1 &&
                    <Entity
                        entity={envelopesSelected.rowsData.length === 1 ? 'modal.deprogram.message_single' : 'modal.deprogram.message_many'}
                        namespace={nsEnvelope}
                        data={{ number: envelopesSelected.rowsData.length }}
                    />
                }
                {step === 2 &&
                    <div className='text-center'>
                        {errorMessage &&
                            <Entity namespace={nsEnvelope} entity='modal.deprogram.error.unknown_error' />
                        }
                        {!errorMessage && notDeleted.length &&
                            (
                                <>
                                    <Entity
                                        namespace={nsEnvelope}
                                        entity={notDeleted.length === 1 ? 'modal.deprogram.error.not_deleted_single' : 'modal.deprogram.error.not_deleted_multiple'}
                                    />
                                    <ul>
                                        {notDeleted.map((envelopeNotDeleted) => <li key={envelopeNotDeleted.uuid}>{envelopeNotDeleted}</li>)}
                                    </ul>
                                </>
                            )
                        }
                    </div>
                }
            </ModalBody>
            {step < 2 &&
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={() => {
                        const envelopesIds = { uuids: Object.keys(envelopesSelected.rowIds) };
                        deprogramEnvelopes.mutate({ envelopesIds });
                    }}
                    isLoading={deprogramEnvelopes.isLoading}
                    disableValidation={disabledNext}
                />
            }
            {step === 2 &&
                <FooterClose />
            }
        </ModalContent>
    </Modal>;

}

ModalDeprogramEnvelopes.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func
};

export default ModalDeprogramEnvelopes;